/**
 * @fileoverview O modal de um quadro do Multi unidades: o que o número
 * significa, quem pesou nele (ofensores) e quais tickets entraram.
 *
 * Gráficos e cartões "Ativos agora" e "Sem resposta": pede ao servidor
 * (/dashboard/multi-unidades/card-details), que usa a mesma regra do número.
 * Cartões do ART: o ranking que a tela já tem, e os tickets de cada pessoa
 * pelo detalhe do atendente nas últimas 24 h.
 */

import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import MenuItem from "@material-ui/core/MenuItem";
import { X as XIcon, ChevronDown, ChevronRight } from "lucide-react";
import api from "../../../../services/api";
import ModernSelect from "../../../../components/ModernSelect";
import { CARD_INFO } from "../cardInfo";
import { fmtNumberPtBR, formatARTMinutes, fmtPeriod, secToShort, STATUS_LABEL } from "../format";

// Os meses (ou dias) do gráfico do quadro, do mais recente ao mais antigo.
const periodsOf = (cardKey, charts) => {
  const info = CARD_INFO[cardKey];
  if (!info || !charts) return [];
  if (info.kind === "daily") return (charts.totalDaily || []).map(p => p.date).reverse();
  if (info.kind === "monthly") return (charts[cardKey] || []).map(p => p.month).reverse();
  return [];
};

const CardDetailsDialog = ({
  cardKey,
  onClose,
  tokens,
  actionBtnClass,
  scopeParams,
  charts,
  artRanking,
  errorText,
  refreshKey
}) => {
  // O último quadro aberto continua desenhado enquanto o modal fecha (a
  // animação de saída); o pedido só acontece com o modal aberto. Derivado na
  // própria renderização: ao trocar de quadro, nada usa as regras do anterior.
  const lastKeyRef = useRef(cardKey);
  if (cardKey) lastKeyRef.current = cardKey;
  const shownKey = cardKey || lastKeyRef.current;
  const info = shownKey ? CARD_INFO[shownKey] : null;
  const periods = periodsOf(shownKey, charts);
  // O período escolhido vale para o quadro em que foi escolhido e enquanto o
  // gráfico o mostrar; senão, o mais recente.
  const [chosen, setChosen] = useState({ card: null, period: "" });
  const period =
    chosen.card === shownKey && periods.includes(chosen.period) ? chosen.period : periods[0] || "";
  // Os cartões ao vivo acompanham a atualização da tela, para não divergir do
  // número do cartão; os gráficos, não (o mês não muda com ela).
  const liveRefresh = info && info.kind === "live" ? refreshKey : "";
  const requestKey = `${shownKey}|${period}|${liveRefresh}`;
  const [state, setState] = useState({ key: null, error: null, data: null });
  // A resposta de um pedido anterior (outro quadro, outro mês) não entra.
  const generationRef = useRef(0);

  useEffect(() => {
    generationRef.current += 1;
    if (!cardKey || !info || info.kind === "art") return;
    if ((info.kind === "monthly" || info.kind === "daily") && !period) return;
    const generation = generationRef.current;
    const params = { card: shownKey, ...scopeParams };
    if (period && info.kind !== "live") params.period = period;
    api
      .get("/dashboard/multi-unidades/card-details", { params })
      .then(r => {
        if (generation === generationRef.current) setState({ key: requestKey, error: null, data: r.data });
      })
      .catch(e => {
        if (generation === generationRef.current)
          setState({ key: requestKey, error: errorText(e, "Erro ao carregar o detalhe."), data: null });
      });
    // requestKey resume quadro, período e atualização.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardKey, requestKey, scopeParams, errorText]);

  if (!info) return null;
  // Só vale o que foi pedido para este quadro e período.
  const current = state.key === requestKey ? state : { error: null, data: null };
  const noPeriod = (info.kind === "monthly" || info.kind === "daily") && !period;

  const cell = { fontSize: 12.5, color: tokens.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  const muted = { fontSize: 11.5, color: tokens.textMuted };
  const headCell = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: tokens.textMuted };
  const section = { padding: "16px 24px", borderTop: "1px solid " + tokens.cardBorder };
  const sectionTitle = { fontSize: 12, fontWeight: 700, color: tokens.textSecondary, marginBottom: 10 };
  const row = cols => ({ display: "grid", gridTemplateColumns: cols, gap: 10, padding: "8px 4px", alignItems: "center" });

  const periodLabel =
    info.kind === "live" ? "agora" : info.kind === "art" ? "últimas 24 h" : fmtPeriod(period);

  return (
    <Dialog
      open={!!cardKey}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          background: tokens.modalBg,
          border: "1px solid " + tokens.cardBorder,
          borderRadius: 14,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)"
        }
      }}
    >
      <div
        style={{
          padding: "18px 24px",
          background: tokens.modalHeaderBg,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ ...headCell, fontSize: 11, letterSpacing: "0.08em", marginBottom: 4 }}>Detalhe · {periodLabel}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: tokens.textPrimary }}>{info.title}</div>
          <div style={{ fontSize: 12.5, color: tokens.textSecondary, marginTop: 6, lineHeight: 1.5 }}>{info.explanation}</div>
        </div>
        <button className={actionBtnClass} style={{ padding: 6 }} onClick={onClose} title="Fechar">
          <XIcon size={16} />
        </button>
      </div>

      {(info.kind === "monthly" || info.kind === "daily") && periods.length > 0 && (
        <div style={{ ...section, display: "flex", alignItems: "center", gap: 12 }}>
          <ModernSelect
            label={info.kind === "daily" ? "Dia" : "Mês"}
            value={period}
            onChange={e => setChosen({ card: shownKey, period: e.target.value })}
            size="small"
            formControlProps={{ style: { minWidth: 160 } }}
          >
            {periods.map(p => (
              <MenuItem key={p} value={p}>
                {fmtPeriod(p)}
              </MenuItem>
            ))}
          </ModernSelect>
        </div>
      )}

      {info.kind === "art" ? (
        <ArtRanking
          ranking={artRanking}
          worstFirst={!!info.worstFirst}
          tokens={tokens}
          scopeParams={scopeParams}
          errorText={errorText}
          styles={{ cell, muted, headCell, section, sectionTitle, row }}
        />
      ) : noPeriod ? (
        <div style={{ ...section, ...muted }}>Sem dados no gráfico.</div>
      ) : current.error ? (
        <div style={{ ...section, fontSize: 12.5, color: tokens.breachText }}>{current.error}</div>
      ) : !current.data ? (
        <div style={{ ...section, ...muted }}>Carregando…</div>
      ) : (
        <CardContent data={current.data} info={info} tokens={tokens} styles={{ cell, muted, headCell, section, sectionTitle, row }} />
      )}
    </Dialog>
  );
};

const CardContent = ({ data, info, tokens, styles }) => {
  const { cell, muted, headCell, section, sectionTitle, row } = styles;
  const { summary, offenders, tickets } = data;
  const valueText = v => (v == null ? "—" : secToShort(v));
  // O número do quadro: duração nas médias e máximos; quantidade nas contagens.
  const summaryText = summary.aggregate === "count" ? fmtNumberPtBR(summary.value) : secToShort(summary.value);
  const showValue = !!info.valueLabel;
  const timed = summary.aggregate !== "count" || showValue;

  return (
    <>
      <div style={{ ...section, display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
        <span style={{ fontSize: 26, fontWeight: 700, color: tokens.textPrimary }}>{summaryText}</span>
        <span style={muted}>
          {fmtNumberPtBR(summary.count)} ticket(s){summary.aggregate !== "count" ? " no cálculo" : ""}
        </span>
      </div>

      <div style={section}>
        <div style={sectionTitle}>Quem pesou</div>
        {offenders.length === 0 ? (
          <div style={muted}>Nenhum ticket.</div>
        ) : (
          <>
            <div style={row(timed ? "minmax(160px,1fr) minmax(120px,0.8fr) 70px 90px 90px" : "minmax(160px,1fr) minmax(120px,0.8fr) 70px")}>
              <span style={headCell}>Atendente</span>
              <span style={headCell}>Conta</span>
              <span style={{ ...headCell, textAlign: "right" }}>Tickets</span>
              {timed && <span style={{ ...headCell, textAlign: "right" }}>Pior</span>}
              {timed && <span style={{ ...headCell, textAlign: "right" }}>Média</span>}
            </div>
            {offenders.map(o => (
              <div
                key={`${o.companyId}-${o.userId}-${o.aiAgentId}`}
                style={{ ...row(timed ? "minmax(160px,1fr) minmax(120px,0.8fr) 70px 90px 90px" : "minmax(160px,1fr) minmax(120px,0.8fr) 70px"), borderTop: "1px solid " + tokens.cardBorder }}
              >
                <span style={{ ...cell, color: o.kind === "none" ? tokens.textMuted : tokens.textPrimary }} title={o.name}>
                  {o.name}
                </span>
                <span style={{ ...cell, color: tokens.textSecondary }} title={o.companyName}>
                  {o.companyName}
                </span>
                <span style={{ ...cell, textAlign: "right" }}>{fmtNumberPtBR(o.count)}</span>
                {timed && <span style={{ ...cell, textAlign: "right" }}>{valueText(o.maxSeconds)}</span>}
                {timed && <span style={{ ...cell, textAlign: "right" }}>{valueText(o.avgSeconds)}</span>}
              </div>
            ))}
          </>
        )}
      </div>

      <div style={section}>
        <div style={sectionTitle}>
          Tickets{" "}
          <span style={{ ...muted, fontWeight: 500 }}>
            {tickets.length < summary.count ? `· os ${tickets.length} primeiros de ${fmtNumberPtBR(summary.count)}` : ""}
          </span>
        </div>
        {tickets.length === 0 ? (
          <div style={muted}>Nenhum ticket.</div>
        ) : (
          <>
            <div style={row("minmax(170px,1.2fr) minmax(110px,0.8fr) minmax(120px,0.9fr) 80px 110px")}>
              <span style={headCell}>Cliente</span>
              <span style={headCell}>Conta</span>
              <span style={headCell}>Atendente</span>
              <span style={headCell}>Situação</span>
              <span style={{ ...headCell, textAlign: "right" }}>{info.valueLabel || "Aberto em"}</span>
            </div>
            {tickets.map(t => (
              <div
                key={t.id}
                style={{ ...row("minmax(170px,1.2fr) minmax(110px,0.8fr) minmax(120px,0.9fr) 80px 110px"), borderTop: "1px solid " + tokens.cardBorder }}
              >
                <span style={{ minWidth: 0 }}>
                  <span style={{ ...cell, display: "block" }} title={t.contactName}>
                    {t.contactName}
                  </span>
                  <span style={{ ...muted, fontSize: 11 }}>
                    #{t.id}
                    {t.contactNumber ? ` · ${t.contactNumber}` : ""}
                  </span>
                </span>
                <span style={{ ...cell, color: tokens.textSecondary }} title={t.companyName}>
                  {t.companyName}
                </span>
                <span style={{ ...cell, color: t.ownerKind === "none" ? tokens.textMuted : tokens.textPrimary }} title={t.ownerName}>
                  {t.ownerName}
                </span>
                <span style={{ ...cell, color: tokens.textSecondary }}>{STATUS_LABEL[t.status] || t.status}</span>
                <span style={{ ...cell, textAlign: "right" }}>
                  {info.valueLabel ? valueText(t.valueSeconds) : new Date(t.createdAt).toLocaleDateString("pt-BR")}
                </span>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
};

// ART das últimas 24 h: o ranking que a tela já tem; por conta, os tickets da
// pessoa pelo detalhe do atendente (period=24h).
const ArtRanking = ({ ranking, worstFirst, tokens, scopeParams, errorText, styles }) => {
  const { cell, muted, headCell, section, sectionTitle, row } = styles;
  const [openName, setOpenName] = useState(null);
  const [drill, setDrill] = useState({});
  const generationRef = useRef(0);
  // Recolhe só com outro escopo: o polling da tela troca o ranking a cada 60 s.
  useEffect(() => {
    generationRef.current += 1;
    setDrill({});
    setOpenName(null);
  }, [scopeParams]);

  const rows = [...(ranking || [])].sort((a, b) => (worstFirst ? b.avgMinutes - a.avgMinutes : a.avgMinutes - b.avgMinutes));

  const loadTickets = b => {
    const key = `${b.userId}-${b.companyId}`;
    if (drill[key] && !drill[key].error) return;
    const generation = generationRef.current;
    setDrill(d => ({ ...d, [key]: { loading: true } }));
    api
      .get("/dashboard/multi-unidades/attendant-tickets", { params: { userId: b.userId, companyId: b.companyId, period: "24h", ...scopeParams } })
      .then(r => {
        if (generation === generationRef.current) setDrill(d => ({ ...d, [key]: { tickets: r.data?.tickets || [] } }));
      })
      .catch(e => {
        if (generation === generationRef.current) setDrill(d => ({ ...d, [key]: { error: errorText(e, "Erro ao carregar os tickets.") } }));
      });
  };

  if (rows.length === 0) return <div style={{ ...section, ...muted }}>Ninguém respondeu nas últimas 24 horas.</div>;

  return (
    <div style={section}>
      <div style={sectionTitle}>{worstFirst ? "Do mais lento ao mais rápido" : "Do mais rápido ao mais lento"}</div>
      <div style={row("minmax(180px,1fr) 100px 90px")}>
        <span style={headCell}>Atendente</span>
        <span style={{ ...headCell, textAlign: "right" }}>ART</span>
        <span style={{ ...headCell, textAlign: "right" }}>Respostas</span>
      </div>
      {rows.map(r => {
        const open = openName === r.name;
        return (
          <div key={r.name} style={{ borderTop: "1px solid " + tokens.cardBorder }}>
            <button
              type="button"
              onClick={() => setOpenName(open ? null : r.name)}
              style={{ ...row("minmax(180px,1fr) 100px 90px"), width: "100%", background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
            >
              <span style={{ ...cell, display: "inline-flex", alignItems: "center", gap: 6 }}>
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                {r.name}
              </span>
              <span style={{ ...cell, textAlign: "right" }}>{formatARTMinutes(r.avgMinutes)}</span>
              <span style={{ ...cell, textAlign: "right" }}>{fmtNumberPtBR(r.pairCount)}</span>
            </button>
            {open &&
              (r.breakdown || []).map(b => {
                const key = `${b.userId}-${b.companyId}`;
                const d = drill[key];
                const canDrill = !!b.userId;
                return (
                  <div key={key} style={{ padding: "0 4px 8px 26px" }}>
                    <div style={{ ...row("minmax(160px,1fr) 100px 90px"), padding: "4px 0" }}>
                      {canDrill ? (
                        <button
                          type="button"
                          onClick={() => loadTickets(b)}
                          style={{ ...cell, background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left", color: tokens.accent }}
                          title="Ver os tickets"
                        >
                          {b.companyName}
                        </button>
                      ) : (
                        <span style={{ ...cell, color: tokens.textSecondary }}>{b.companyName}</span>
                      )}
                      <span style={{ ...cell, textAlign: "right" }}>{formatARTMinutes(b.avgMinutes)}</span>
                      <span style={{ ...cell, textAlign: "right" }}>{fmtNumberPtBR(b.pairCount)}</span>
                    </div>
                    {d && d.loading && <div style={muted}>Carregando tickets…</div>}
                    {d && d.error && <div style={{ ...muted, color: tokens.breachText }}>{d.error}</div>}
                    {d && d.tickets && d.tickets.length === 0 && <div style={muted}>Nenhum ticket nas últimas 24 horas.</div>}
                    {d &&
                      d.tickets &&
                      d.tickets.map(t => (
                        <div key={t.ticketId} style={{ ...row("minmax(160px,1fr) 100px 90px"), padding: "4px 0", borderTop: "1px solid " + tokens.cardBorder }}>
                          <span style={cell} title={t.contactName}>
                            {t.contactName} <span style={muted}>#{t.ticketId}</span>
                          </span>
                          <span style={{ ...cell, textAlign: "right" }}>{t.responseTime != null ? secToShort(t.responseTime) : "sem resposta"}</span>
                          <span style={{ ...cell, textAlign: "right", color: tokens.textSecondary }}>{STATUS_LABEL[t.status] || t.status}</span>
                        </div>
                      ))}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
};

export default CardDetailsDialog;
